/**
 * Evidence Request Generator
 *
 * Evaluates estate data and produces a personalised list of documents
 * required to complete the probate and inheritance tax process.
 *
 * Pure function — no React, no side effects.
 * Does NOT change any tax calculations or probate logic.
 */

import type {
    EstateAssets,
    EstateProfile,
    PropertyAsset,
    BankAccountAsset,
    InvestmentAsset,
    PensionAsset,
    DebtLiability,
    LifetimeGift,
} from '../../types';

// ── Types ──────────────────────────────────────────────────────────────

export type EvidenceCategory =
    | 'property'
    | 'bank_account'
    | 'investment'
    | 'pension'
    | 'debt'
    | 'lifetime_gift'
    | 'ownership'
    | 'relief'
    | 'life_insurance'
    | 'foreign_asset'
    | 'general';

export interface EvidenceRequest {
    id: string;
    category: EvidenceCategory;
    /** Human-readable group heading, e.g. "12 High Street" */
    group: string;
    /** Individual document description */
    document: string;
    /** Why this document is needed */
    reason: string;
    /** Who to contact to obtain it (optional) */
    contactHint?: string;
    /** Priority: higher = more important */
    priority: 'high' | 'medium' | 'low';
}

export interface EvidenceRequestResult {
    items: EvidenceRequest[];
    generatedAt: string;
    totalCount: number;
    byCategoryCount: Record<EvidenceCategory, number>;
}

// ── Helpers ────────────────────────────────────────────────────────────

let idCounter = 0;
function nextId(prefix: string): string {
    return `${prefix}_${++idCounter}`;
}

function propLabel(p: PropertyAsset): string {
    return p.label || p.address || 'Unnamed property';
}

// ── Property Rules ─────────────────────────────────────────────────────

function propertyEvidence(properties: PropertyAsset[]): EvidenceRequest[] {
    const items: EvidenceRequest[] = [];

    for (const p of properties) {
        const label = propLabel(p);

        items.push({
            id: nextId('prop'),
            category: 'property',
            group: label,
            document: 'Professional valuation as at date of death',
            reason: 'HMRC requires a date-of-death valuation for all property assets. A RICS Red Book valuation is recommended.',
            contactHint: 'Instruct a local RICS-registered surveyor or estate agent',
            priority: 'high',
        });

        items.push({
            id: nextId('prop'),
            category: 'property',
            group: label,
            document: 'Land Registry title register',
            reason: 'Confirms ownership type, share percentages, and any registered charges.',
            contactHint: 'Download from HM Land Registry (gov.uk) or request via solicitor',
            priority: 'high',
        });

        if (p.has_mortgage) {
            items.push({
                id: nextId('prop'),
                category: 'property',
                group: label,
                document: 'Mortgage redemption statement',
                reason: 'Confirms the outstanding mortgage balance as at date of death for IHT deduction.',
                contactHint: p.mortgage_lender
                    ? `Contact ${p.mortgage_lender}`
                    : 'Contact the mortgage lender',
                priority: 'high',
            });
        }

        if (p.ownership_type === 'joint_tenants' || p.ownership_type === 'tenants_in_common') {
            items.push({
                id: nextId('prop'),
                category: 'ownership',
                group: label,
                document: 'Evidence of ownership share',
                reason: 'Confirms the deceased\'s beneficial share. Required where property is jointly owned.',
                contactHint: 'Check title deeds, declaration of trust, or conveyancing documents',
                priority: 'medium',
            });
        }
    }

    return items;
}

// ── Bank Account Rules ─────────────────────────────────────────────────

function bankAccountEvidence(accounts: BankAccountAsset[]): EvidenceRequest[] {
    const items: EvidenceRequest[] = [];

    for (const a of accounts) {
        const label = a.provider || a.label || 'Bank account';

        items.push({
            id: nextId('bank'),
            category: 'bank_account',
            group: label,
            document: 'Official date-of-death balance confirmation',
            reason: 'Banks issue a formal letter confirming the balance at date of death. Required for HMRC.',
            contactHint: `Contact ${a.provider || 'the bank'} bereavement team`,
            priority: 'high',
        });

        if (a.ownership === 'joint') {
            items.push({
                id: nextId('bank'),
                category: 'ownership',
                group: label,
                document: 'Account mandate or ownership evidence',
                reason: 'Confirms the deceased\'s beneficial share of the joint account.',
                contactHint: `Request from ${a.provider || 'the bank'}`,
                priority: 'medium',
            });
        }
    }

    return items;
}

// ── Investment Rules ───────────────────────────────────────────────────

function investmentEvidence(investments: InvestmentAsset[]): EvidenceRequest[] {
    const items: EvidenceRequest[] = [];

    for (const inv of investments) {
        const label = inv.provider || inv.label || 'Investment';

        items.push({
            id: nextId('inv'),
            category: 'investment',
            group: label,
            document: 'Portfolio valuation at date of death',
            reason: 'HMRC requires the market value of investments as at the date of death.',
            contactHint: `Contact ${inv.provider || 'the investment provider'}`,
            priority: 'high',
        });

        items.push({
            id: nextId('inv'),
            category: 'investment',
            group: label,
            document: 'Share certificates or unit confirmation (if applicable)',
            reason: 'Needed to transfer or sell holdings during estate administration.',
            contactHint: `Request from ${inv.provider || 'the registrar'}`,
            priority: 'low',
        });

        if (inv.ownership === 'joint') {
            items.push({
                id: nextId('inv'),
                category: 'ownership',
                group: label,
                document: 'Evidence of ownership share',
                reason: 'Confirms the deceased\'s beneficial share of the joint investment.',
                contactHint: `Contact ${inv.provider || 'the provider'}`,
                priority: 'medium',
            });
        }
    }

    return items;
}

// ── Pension Rules ──────────────────────────────────────────────────────

function pensionEvidence(pensions: PensionAsset[]): EvidenceRequest[] {
    const items: EvidenceRequest[] = [];

    for (const pen of pensions) {
        const label = pen.provider || pen.label || 'Pension';

        items.push({
            id: nextId('pen'),
            category: 'pension',
            group: label,
            document: 'Confirmation of death benefit nomination',
            reason: 'Confirms who the pension benefits are payable to and whether the scheme has discretion.',
            contactHint: `Contact ${pen.provider || 'the pension provider'}`,
            priority: 'high',
        });

        items.push({
            id: nextId('pen'),
            category: 'pension',
            group: label,
            document: 'Statement confirming whether pension is inside or outside the estate',
            reason: 'Determines whether the pension value should be included in the IHT calculation.',
            contactHint: `Contact ${pen.provider || 'the pension provider'}`,
            priority: 'high',
        });
    }

    return items;
}

// ── Lifetime Gift Rules ────────────────────────────────────────────────

function giftEvidence(gifts: LifetimeGift[]): EvidenceRequest[] {
    const items: EvidenceRequest[] = [];

    if (gifts.length === 0) return items;

    // Group evidence at the gift level
    for (const g of gifts) {
        const label = g.description || `Gift of £${(g.amount || 0).toLocaleString('en-GB')}`;
        const dateLabel = g.date ? ` (${g.date})` : '';

        items.push({
            id: nextId('gift'),
            category: 'lifetime_gift',
            group: `${label}${dateLabel}`,
            document: 'Bank statements confirming the transfer',
            reason: 'HMRC requires evidence of the date and amount of each gift within 7 years of death.',
            priority: 'high',
        });

        items.push({
            id: nextId('gift'),
            category: 'lifetime_gift',
            group: `${label}${dateLabel}`,
            document: 'Written confirmation from the recipient',
            reason: 'Supports the gift declaration with independent confirmation of receipt.',
            priority: 'medium',
        });

        if (g.is_regular_from_income) {
            items.push({
                id: nextId('gift'),
                category: 'lifetime_gift',
                group: `${label}${dateLabel}`,
                document: 'Income and expenditure records supporting the exemption',
                reason: 'The normal expenditure out of income exemption (IHTA 1984 s21) requires evidence that gifts were made from surplus income.',
                contactHint: 'Gather tax returns, pension statements, bank statements showing regular income and outgoings',
                priority: 'high',
            });
        }
    }

    return items;
}

// ── Debt Rules ─────────────────────────────────────────────────────────

function debtEvidence(debts: DebtLiability[]): EvidenceRequest[] {
    const items: EvidenceRequest[] = [];

    for (const d of debts) {
        const label = d.institution || d.label || 'Debt';
        const typeLabel = d.type === 'credit_card' ? 'Credit card balance confirmation'
            : d.type === 'loan' ? 'Loan balance confirmation'
            : d.type === 'care_fees' ? 'Care fees balance confirmation'
            : d.type === 'funeral' ? 'Funeral invoice'
            : 'Balance confirmation';

        items.push({
            id: nextId('debt'),
            category: 'debt',
            group: label,
            document: typeLabel,
            reason: 'Outstanding debts are deducted from the estate value for IHT purposes. The balance at date of death is required.',
            contactHint: d.institution ? `Contact ${d.institution}` : undefined,
            priority: 'medium',
        });
    }

    return items;
}

// ── Relief Evidence ────────────────────────────────────────────────────

function reliefEvidence(assets: EstateAssets, profile: EstateProfile): EvidenceRequest[] {
    const items: EvidenceRequest[] = [];

    // BPR assets
    for (const bpr of assets.bpr_assets ?? []) {
        if (bpr.estimated_value && bpr.estimated_value > 0) {
            items.push({
                id: nextId('bpr'),
                category: 'relief',
                group: bpr.label || 'Business asset',
                document: 'Business accounts and ownership records',
                reason: 'Business Property Relief requires evidence of qualifying use, ownership duration (minimum 2 years), and that the business is trading.',
                priority: 'high',
            });
            items.push({
                id: nextId('bpr'),
                category: 'relief',
                group: bpr.label || 'Business asset',
                document: 'Professional business valuation',
                reason: 'HMRC may challenge the claimed value. A professional valuation strengthens the relief claim.',
                priority: 'medium',
            });
        }
    }

    // APR assets
    for (const apr of assets.apr_assets ?? []) {
        if (apr.estimated_value && apr.estimated_value > 0) {
            items.push({
                id: nextId('apr'),
                category: 'relief',
                group: apr.label || 'Agricultural property',
                document: 'Agricultural valuation and tenancy records',
                reason: 'Agricultural Property Relief requires evidence of agricultural use, ownership duration, and tenancy type.',
                priority: 'high',
            });
        }
    }

    // Predeceased spouse (TNRB)
    const sp = profile.iht_details?.predeceased_spouse;
    if (sp?.exists === true) {
        items.push({
            id: nextId('tnrb'),
            category: 'relief',
            group: 'Transferable Nil Rate Band',
            document: 'First spouse\'s death certificate',
            reason: 'Required to claim the transferred NRB allowance (HMRC form IHT402).',
            priority: 'high',
        });
        items.push({
            id: nextId('tnrb'),
            category: 'relief',
            group: 'Transferable Nil Rate Band',
            document: 'First spouse\'s grant of probate and IHT return',
            reason: 'Confirms how much of the first spouse\'s NRB was used, to calculate the transferable percentage.',
            contactHint: 'Check with the original solicitor or request copies from the Probate Registry',
            priority: 'high',
        });
    }

    // Charitable bequests
    if (profile.iht_details?.charitable_bequests?.exists === true) {
        items.push({
            id: nextId('charity'),
            category: 'relief',
            group: 'Charitable Bequests',
            document: 'Evidence of charitable gifts in the will',
            reason: 'Required to support any charitable exemption claim and the potential 36% reduced IHT rate.',
            priority: 'medium',
        });
    }

    return items;
}

// ── Life Insurance ─────────────────────────────────────────────────────

function lifeInsuranceEvidence(assets: EstateAssets): EvidenceRequest[] {
    const items: EvidenceRequest[] = [];

    if (assets.life_insurance?.exists !== true) return items;

    if (assets.life_insurance.written_in_trust === true) {
        items.push({
            id: nextId('ins'),
            category: 'life_insurance',
            group: 'Life Insurance',
            document: 'Trust deed for the life insurance policy',
            reason: 'Confirms the policy is held in trust and therefore excluded from the estate.',
            contactHint: 'Contact the insurer or the original broker',
            priority: 'high',
        });
    } else {
        items.push({
            id: nextId('ins'),
            category: 'life_insurance',
            group: 'Life Insurance',
            document: 'Policy payout confirmation',
            reason: 'Confirms the payout amount and whether the policy is held in trust.',
            contactHint: 'Contact the insurer',
            priority: 'high',
        });
    }

    return items;
}

// ── Foreign Assets ─────────────────────────────────────────────────────

function foreignAssetEvidence(assets: EstateAssets): EvidenceRequest[] {
    const items: EvidenceRequest[] = [];

    if (assets.foreign_assets?.exists !== true) return items;

    const countries = assets.foreign_assets.countries ?? [];
    const countryText = countries.length > 0 ? ` (${countries.join(', ')})` : '';

    items.push({
        id: nextId('foreign'),
        category: 'foreign_asset',
        group: `Foreign Assets${countryText}`,
        document: 'Valuations for all foreign assets at date of death',
        reason: 'HMRC requires date-of-death values for foreign assets included in the UK estate.',
        priority: 'high',
    });

    items.push({
        id: nextId('foreign'),
        category: 'foreign_asset',
        group: `Foreign Assets${countryText}`,
        document: 'Local succession/probate requirements',
        reason: 'Separate probate or succession procedures may be required in each jurisdiction.',
        contactHint: 'Seek local legal advice in each relevant country',
        priority: 'medium',
    });

    return items;
}

// ── General / Always Required ──────────────────────────────────────────

function generalEvidence(profile: EstateProfile): EvidenceRequest[] {
    const items: EvidenceRequest[] = [];

    items.push({
        id: nextId('gen'),
        category: 'general',
        group: 'General',
        document: 'Certified copy of the death certificate',
        reason: 'Required for all probate applications, bank notifications, and HMRC submissions.',
        contactHint: 'Order from the General Register Office or the local register office',
        priority: 'high',
    });

    if (profile.representatives?.will_exists === 'yes') {
        items.push({
            id: nextId('gen'),
            category: 'general',
            group: 'General',
            document: 'Original signed will and any codicils',
            reason: 'The original will is required for the probate application.',
            contactHint: 'Check with the deceased\'s solicitor, bank safe deposit, or home',
            priority: 'high',
        });
    } else if (profile.representatives?.will_exists === 'no') {
        items.push({
            id: nextId('gen'),
            category: 'general',
            group: 'General',
            document: 'Confirmation that no will exists (intestacy)',
            reason: 'Check all likely locations before confirming intestacy. Letters of Administration will be needed.',
            contactHint: 'Check solicitors, bank safe deposit boxes, and the home',
            priority: 'high',
        });
    }

    return items;
}

// ── Main Generator ─────────────────────────────────────────────────────

export function generateEvidenceRequests(
    assets: EstateAssets,
    profile: EstateProfile,
): EvidenceRequestResult {
    // Reset counter for stable IDs within a single generation
    idCounter = 0;

    const items: EvidenceRequest[] = [
        ...propertyEvidence(assets.properties ?? []),
        ...bankAccountEvidence(assets.bank_accounts ?? []),
        ...investmentEvidence(assets.investments ?? []),
        ...pensionEvidence(assets.pensions ?? []),
        ...giftEvidence(assets.lifetime_gifts ?? []),
        ...debtEvidence(assets.debts ?? []),
        ...reliefEvidence(assets, profile),
        ...lifeInsuranceEvidence(assets),
        ...foreignAssetEvidence(assets),
        ...generalEvidence(profile),
    ];

    // Sort: high priority first, then by category
    const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
    items.sort((a, b) => {
        const pDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        if (pDiff !== 0) return pDiff;
        return a.category.localeCompare(b.category);
    });

    const byCategoryCount = {} as Record<EvidenceCategory, number>;
    for (const item of items) {
        byCategoryCount[item.category] = (byCategoryCount[item.category] ?? 0) + 1;
    }

    return {
        items,
        generatedAt: new Date().toISOString(),
        totalCount: items.length,
        byCategoryCount,
    };
}

// ── Email Generator ────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<EvidenceCategory, string> = {
    property: 'Property',
    bank_account: 'Bank Accounts',
    investment: 'Investments',
    pension: 'Pensions',
    debt: 'Debts & Liabilities',
    lifetime_gift: 'Lifetime Gifts',
    ownership: 'Ownership Verification',
    relief: 'Tax Reliefs & Allowances',
    life_insurance: 'Life Insurance',
    foreign_asset: 'Foreign Assets',
    general: 'General',
};

export function generateEvidenceEmailText(
    result: EvidenceRequestResult,
    caseName?: string,
): string {
    const lines: string[] = [];

    lines.push('Subject: Documents required for estate administration');
    lines.push('');
    lines.push(`Dear [Recipient],`);
    lines.push('');
    lines.push(`I am writing in connection with the estate of ${caseName || '[Deceased Name]'}.`);
    lines.push('');
    lines.push('To progress the probate and inheritance tax process, we need to obtain the following documents. I would be grateful if you could assist with providing any that are within your records.');
    lines.push('');

    // Group by category
    const categories = [...new Set(result.items.map(i => i.category))];
    for (const cat of categories) {
        const catItems = result.items.filter(i => i.category === cat);
        lines.push(`--- ${CATEGORY_LABELS[cat] ?? cat} ---`);
        lines.push('');

        // Sub-group by group label
        const groups = [...new Set(catItems.map(i => i.group))];
        for (const group of groups) {
            const groupItems = catItems.filter(i => i.group === group);
            if (groups.length > 1 || group !== CATEGORY_LABELS[cat]) {
                lines.push(`${group}:`);
            }
            for (const item of groupItems) {
                lines.push(`  - ${item.document}`);
                if (item.contactHint) {
                    lines.push(`    (${item.contactHint})`);
                }
            }
            lines.push('');
        }
    }

    lines.push('If you have any questions or need clarification, please do not hesitate to get in touch.');
    lines.push('');
    lines.push('Kind regards,');
    lines.push('[Your Name]');

    return lines.join('\n');
}
